<script setup>
import HazardIcon from '@/Components/HazardIcon.vue';
import { computed, inject, reactive, ref, useId, watch } from 'vue';
import DifficultyIcon from '@/Components/DifficultyIcon.vue';
import { iconSetForUse, resolveDifficultyColor } from '@/lib/trailIconSet.js';
import { groomingFreshness } from '@/lib/grooming.js';
import { publicHazardTone } from '@/lib/publicHazard.js';
import { trailsStatusOf } from '@/lib/trailsStatus.js';
import { wetnessTone } from '@/lib/wetnessTone.js';
import { distanceText, styleVars, wetnessLine } from './shared.js';
import { DIFFICULTY_KEYS, difficultyLabel, primaryUseLabel } from '@/lib/trailLabels.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// Every group the server sent, optionally narrowed by the block's
// `network_ids`. An allow-list drops the "Other trails" bucket, whose
// network_id is null.
// No trails is no block for a visitor, heading included; the editor
// keeps its line (Mus, 2026-10-11). A search that matches nothing is
// an answer to the visitor and still says so.
const editing = inject('tiqEditorCanvas', false);
const allGroups = computed(() => (Array.isArray(props.site_data?.trail_groups) ? props.site_data.trail_groups : []));
const pickedNetworkIds = computed(() => (props.config.network_ids || []).map(Number));
const configGroups = computed(() => {
    if (!pickedNetworkIds.value.length) return allGroups.value;
    return allGroups.value.filter((g) => g.network_id !== null && pickedNetworkIds.value.includes(Number(g.network_id)));
});

// One reading of "how is this trail", shared with every other surface
// (lib/trailsStatus.js): maintenance counts as closed, and a single
// caution trail is a caution.
function toneOf(condition) {
    const status = trailsStatusOf([condition]).status;
    if (status === 'closed') return 'closed';
    if (status === 'caution' || status === 'partial') return 'caution';
    return 'open';
}
const TONE_LABELS = { open: 'Open', caution: 'Caution', closed: 'Closed' };
const CONDITION_LABELS = {
    open: 'Open', caution: 'Caution', partial: 'Some closed', closed: 'Closed', maintenance: 'Maintenance',
};
const conditionLabel = (c) => CONDITION_LABELS[c] || c || '';

// A group's badge is its trails, read with the same rule — it used to
// be the network's own setting, so one caution trail badged a whole
// open group.
const groupTone = (group) => toneOf(group.trails?.length
    ? trailsStatusOf(group.trails.map((t) => t.condition)).status
    : group.network_condition);
const groupLabel = (group) => conditionLabel(group.trails?.length
    ? trailsStatusOf(group.trails.map((t) => t.condition)).status
    : group.network_condition);

// Filter chips, then the optional live search. Both narrow the same
// groups and a group with nothing left in it drops out.
const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'caution', label: 'Caution' },
    { key: 'closed', label: 'Closed' },
];
const activeFilter = ref('all');
const searchVisible = computed(() => props.config.show_search !== false);
const trailSearch = ref('');

const visibleGroups = computed(() => {
    const q = trailSearch.value.trim().toLowerCase();
    return configGroups.value
        .map((g) => {
            let trails = Array.isArray(g.trails) ? g.trails : [];
            if (activeFilter.value !== 'all') trails = trails.filter((t) => toneOf(t.condition) === activeFilter.value);
            if (q && !String(g.network_name || '').toLowerCase().includes(q)) {
                trails = trails.filter((t) => String(t.name || '').toLowerCase().includes(q));
            }
            return { ...g, trails };
        })
        .filter((g) => g.trails.length);
});

// A network folds away behind its heading. The block's `collapsed`
// setting decides how each one starts; a visitor's own tap outranks it
// until the page is reloaded. Typing a search opens every network that
// still has a match, because a hit hidden inside a folded network
// reads as "no such trail".
const groupKey = (group) => String(group.network_id ?? 'none');
const openOverrides = reactive({});
const isOpen = (group) => openOverrides[groupKey(group)] ?? props.config.collapsed !== true;
function toggleGroup(group) { openOverrides[groupKey(group)] = !isOpen(group); }
watch(() => props.config.collapsed, () => { for (const k of Object.keys(openOverrides)) delete openOverrides[k]; });
watch(trailSearch, (q) => {
    if (!q.trim()) return;
    for (const g of visibleGroups.value) openOverrides[groupKey(g)] = true;
});
// useId, not Math.random: the page is rendered on the server and
// hydrated, and a random id would differ between the two — the button's
// aria-controls keeping the server's while anything looking the panel
// up by id asked for the browser's.
const uid = useId();
const panelId = (group) => `th-trails-${uid}-${groupKey(group)}`;

const hasDifficultyIcon = (d) => DIFFICULTY_KEYS.includes(d);
const trailIconSet = (trail) => iconSetForUse(trail.primary_use);
const trailDifficultyColor = (trail) => resolveDifficultyColor(props.org?.difficulty_colors, trailIconSet(trail), trail.difficulty);
const trailDifficulty = (trail) => difficultyLabel(trail.difficulty);
// False when the organization does not publish trail status (SiteData
// strips the status itself; this leaves out the parts that are only it).
const showStatus = computed(() => props.site_data?.show_status !== false);
</script>

<template>
    <section v-if="allGroups.length || editing" :id="config.anchor_id || null" class="th-block th-sec th-trails" :style="styleVars(config)">
        <!-- th-sec on the section, not the wrap: the gap rules beside a
             band (trailhead.css) match a .th-sec SIBLING, and on the wrap
             this list sat half the design's gap under a tinted block. -->
        <div class="th-wrap">
            <div v-if="config.heading" class="th-head">
                <h2 class="th-h2 th-h2--sm">{{ config.heading }}</h2>
            </div>

            <div v-if="allGroups.length" class="th-trails__controls">
                <div v-if="showStatus" class="th-chips th-trails__filters">
                    <button v-for="f in FILTERS" :key="f.key"
                            type="button"
                            class="th-chip th-trails__chip"
                            :class="{ 'is-active': activeFilter === f.key }"
                            :aria-pressed="activeFilter === f.key"
                            @click="activeFilter = f.key">
                        {{ f.label }}
                    </button>
                </div>
                <input v-if="searchVisible"
                       v-model="trailSearch"
                       type="search"
                       inputmode="search"
                       autocomplete="off"
                       placeholder="Search trails or networks"
                       aria-label="Search trails or networks"
                       class="th-input th-trails__search">
            </div>

            <div v-for="group in visibleGroups" :key="group.network_id ?? 'none'" class="th-trails__group">
                <div class="th-trails__group-head" :class="{ 'is-collapsed': !isOpen(group) }">
                    <h3 class="th-h3 th-trails__group-name">
                        <button type="button" class="th-trails__toggle"
                                :aria-expanded="isOpen(group)"
                                :aria-controls="panelId(group)"
                                @click="toggleGroup(group)">
                            <svg class="th-trails__chevron" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M7.21 5.21a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.08l-4.25 4.25a.75.75 0 11-1.06-1.06L10.94 10 7.21 6.27a.75.75 0 010-1.06z" clip-rule="evenodd"/>
                            </svg>
                            <span>{{ group.network_name }}</span>
                            <span class="th-trails__count">{{ group.trails.length }}</span>
                        </button>
                    </h3>
                    <span v-if="showStatus && group.network_id !== null" class="th-pill" :class="`th-pill--${groupTone(group)}`">
                        <span class="th-dot"></span>{{ groupLabel(group) }}
                    </span>
                </div>

                <div v-show="isOpen(group)" :id="panelId(group)" class="th-grid th-trails__grid">
                    <article v-for="t in group.trails" :key="t.id" class="th-card th-trails__card" :class="{ 'th-trails__card--link': t.slug }">
                        <div class="th-trails__card-top">
                            <!-- The name is the link to the trail's own page,
                                 stretched over the card so all of it opens. -->
                            <h4 class="th-h3 th-trails__name">
                                <a v-if="t.slug" :href="`/trails/${t.slug}`" class="th-trails__link">{{ t.name }}</a>
                                <template v-else>{{ t.name }}</template>
                            </h4>
                            <span v-if="showStatus" class="th-pill" :class="`th-pill--${toneOf(t.condition)}`">
                                <span class="th-dot"></span>{{ TONE_LABELS[toneOf(t.condition)] }}
                            </span>
                        </div>
                        <p v-if="showStatus && conditionLabel(t.condition) && conditionLabel(t.condition) !== TONE_LABELS[toneOf(t.condition)]"
                           class="th-trails__condition">
                            {{ conditionLabel(t.condition) }}
                        </p>
                        <p v-if="wetnessLine(t)" class="th-trails__wetness">
                            <span class="th-dot" :class="`th-dot--${wetnessTone(t.wetness_pct)}`"></span>{{ wetnessLine(t) }}
                        </p>
                        <p v-if="t.condition_note" class="th-trails__note">{{ t.condition_note }}</p>
                        <!-- Maintenance items published as hazards on
                             this trail, at their own level. -->
                        <p v-for="h in (t.hazards || [])" :key="h.key || h.id" class="th-trails__hazard">
                            <span class="th-trails__hazard-level"
                                  :style="{ background: publicHazardTone(h.level).soft, color: publicHazardTone(h.level).ink }">{{ h.level_label }}</span>
                            <HazardIcon :hazard="h" :size="14" />
                            {{ h.title }}<template v-if="h.subject"> · {{ h.subject }}</template><template v-if="h.passable_label"> · {{ h.passable_label }}</template>
                        </p>
                        <div class="th-mono th-trails__meta">
                            <span class="th-trails__difficulty">
                                <DifficultyIcon v-if="hasDifficultyIcon(t.difficulty)"
                                                :difficulty="t.difficulty"
                                                :icon-set="trailIconSet(t)"
                                                :color="trailDifficultyColor(t)"
                                                class="th-trails__difficulty-icon"
                                                :title="t.difficulty" />
                                <span v-else-if="t.color" class="th-trails__dot" :style="{ background: t.color }"></span>
                                {{ [distanceText(t), trailDifficulty(t), primaryUseLabel(t.primary_use)].filter(Boolean).join(' · ') }}
                            </span>
                            <span v-if="groomingFreshness(t.groomed_at, t.grooming)" class="th-trails__grooming">
                                {{ groomingFreshness(t.groomed_at, t.grooming).label }}
                            </span>
                        </div>
                    </article>
                </div>
            </div>

            <p v-if="!allGroups.length" class="th-body th-trails__empty">No trails to show yet.</p>
            <p v-else-if="!visibleGroups.length" class="th-body th-trails__empty">
                No trails match that filter.
            </p>
        </div>
    </section>
</template>

<style scoped>
.th-trails__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 20px;
}
.th-trails__search { max-width: 280px; }
.th-trails__group + .th-trails__group { margin-top: clamp(24px, 3vw, 40px); }
.th-trails__group-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 10px;
    margin-bottom: 14px;
    border-bottom: 1px solid var(--th-rule);
}
/* A folded network is one row of a list: the next network sits under
   its rule with the same gap a heading has above its own rule. */
.th-trails__group-head.is-collapsed { margin-bottom: 0; }
.th-trails__group:has(> .is-collapsed) + .th-trails__group { margin-top: 0; }
.th-trails__group:has(> .is-collapsed) + .th-trails__group > .th-trails__group-head { padding-top: 16px; }
.th-trails__group-name { min-width: 0; flex: 1 1 auto; }
.th-trails__toggle {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: baseline;
    gap: 8px;
    width: 100%;
    cursor: pointer;
    font: inherit;
    color: inherit;
}
.th-trails__toggle:focus-visible { outline: 2px solid var(--th-accent, currentColor); outline-offset: 4px; border-radius: 4px; }
.th-trails__chevron {
    width: 0.8em;
    height: 0.8em;
    flex: none;
    align-self: center;
    color: var(--th-meta);
    transform: rotate(90deg);
    transition: transform 0.18s ease;
}
.th-trails__group-head.is-collapsed .th-trails__chevron { transform: none; }
.th-trails__count { font: 400 13px/1 var(--th-mono); color: var(--th-meta); }
.th-trails__grid { --th-min: 290px; }
.th-trails__card { display: flex; flex-direction: column; gap: 10px; }
.th-trails__card--link { position: relative; transition: border-color .15s, box-shadow .15s; }
.th-trails__card--link:hover { border-color: var(--th-field); box-shadow: 0 2px 10px rgba(27, 29, 26, .06); }
.th-trails__link { color: inherit; text-decoration: none; }
.th-trails__link::after { content: ''; position: absolute; inset: 0; border-radius: inherit; }
.th-trails__link:hover { text-decoration: none; }
.th-trails__link:focus-visible { outline: none; }
.th-trails__card--link:has(.th-trails__link:focus-visible) { outline: 2px solid var(--accent); outline-offset: 2px; }
.th-trails__card-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
.th-trails__name { min-width: 0; }
.th-trails__condition { margin: 0; font-weight: 600; font-size: 15px; }
.th-trails__wetness { margin: 0; font-weight: 600; font-size: 15px; display: flex; align-items: center; gap: 8px; }
.th-trails__note { margin: 0; font-size: 15px; color: var(--th-muted); text-wrap: pretty; }
.th-trails__hazard { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 14px; }
.th-trails__hazard-level {
    flex-shrink: 0; font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; padding: 3px 7px; border-radius: 4px;
}
.th-trails__meta {
    margin-top: auto;
    padding-top: 10px;
    border-top: 1px solid var(--th-line-soft);
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}
.th-trails__difficulty { display: inline-flex; align-items: center; gap: 6px; }
.th-trails__difficulty-icon { width: 13px; height: 13px; flex: none; }
.th-trails__dot { width: 10px; height: 10px; border-radius: 50%; flex: none; }
.th-trails__empty { margin-top: 8px; }
</style>
